import axios, { AxiosResponse, AxiosError } from "axios";
import { store } from "../../store/store";
import { Sid } from "../../store/orm/userSession";
// Type Definitions
interface GraphQLResponse {
  data?: {
    productInEnvironment?: {
      extApiUrl?: string;
    };
  };
  errors?: Array<{ message: string }>;
}

interface APIError {
  error: number | string;
  message: string;
  data?: any;
}

interface APIResponse<T = any> {
  code?: string;
  message?: string;
  data?: T;
  error?: number | string;
}

interface RequestPayload {
  [key: string]: any;
  apiurl?: string;
  sid?: string;
}

class APIService {
  private apiUrl: string | null = null;
  private token: Sid = {};
  private readonly graphqlUrl: string =
    "https://kundenportal.crent.de/api/graphql/";

  /**
   * Holt die API URL aus GraphQL
   */
  async fetchApiUrl(appCode: string): Promise<string> {
    try {
      const query = `query {
        productInEnvironment(appCode: "${appCode}", product: "ORM-LF") {
          extApiUrl
        }
      }`;

      const response: AxiosResponse<GraphQLResponse> = await axios.post(
        this.graphqlUrl,
        { query },
      );

      if (response.data?.data?.productInEnvironment?.extApiUrl) {
        this.apiUrl = response.data.data.productInEnvironment.extApiUrl;
        return this.apiUrl;
      }

      throw new Error("API URL konnte nicht abgerufen werden");
    } catch (error) {
      console.error("Fehler beim Abrufen der API URL:", error);
      throw error;
    }
  }

  async fetchApiUrlCDN() {
    try {
      const url = process.env.REACT_APP_API_ENDPOINT + "/api/get_api_url";

      const response: any = await axios.get(url, {});
      this.apiUrl = response.data[0].apiurl;

      return this.apiUrl;
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  /**
   * Setzt das Token für Authentifizierung
   */
  setToken(token: Sid): void {
    this.token = token;
  }

  /**
   * Gibt das aktuelle Token zurück
   */
  getToken(): Sid {
    return this.token;
  }

  /**
   * Gibt die aktuelle API URL zurück
   */
  getApiUrl(): string | null {
    return this.apiUrl;
  }

  /**
   * Gibt die Basis-Headers zurück (unterschiedlich für Dev/Production)
   */
  private getHeaders(language: string = "DE"): Record<string, string> {
    if (process.env.NODE_ENV === "development") {
      const authCode = process.env.REACT_APP_AUTHCODE as string;

      const headers: Record<string, string> = {
        authcode: authCode,
        "Content-Type": "application/json",
        language: store.getState().general.language,
        apiurl: this.apiUrl || "",
        sid: this.token.token || "",
      };

      if (this.token) {
        headers["Authorization"] = `Bearer ${this.token}`;
      }

      return headers;
    } else {
      return {
        "Content-Type": "application/json",
        apiurl: this.apiUrl || "",
        language: store.getState().general.language,
        sid: this.token.token || "",
      };
    }
  }

  /**
   * Bereitet Payload vor (fügt apiurl und sid hinzu)
   */
  private async preparePayload(
    payload: RequestPayload = {},
    language: string | boolean = "DE",
  ): Promise<RequestPayload> {
    if (!this.apiUrl) {
      throw new Error(
        "API URL ist nicht gesetzt. Bitte zuerst fetchApiUrl() aufrufen.",
      );
    }

    const preparedPayload: RequestPayload =
      typeof payload === "object" && payload !== null ? { ...payload } : {};
    if (language) {
      try {
        preparedPayload.language = store.getState().general.language;
      } catch (error) {
        preparedPayload.language = language;
      }
    }

    if (process.env.NODE_ENV === "development") {
    } else {
      preparedPayload.apiurl = this.apiUrl;

      if (this.token) {
        preparedPayload.sid = this.token.token;
      }
    }

    return preparedPayload;
  }

  /**
   * Erstellt die finale URL (Dev: direkt, Production: über Proxy)
   */
  private getUrl(route: string): string {
    const normalizedRoute = this.apiUrl?.endsWith("/") ? route : `/${route}`;

    if (process.env.NODE_ENV === "development") {
      return `${this.apiUrl}${normalizedRoute}`;
    } else {
      // Production verwendet Proxy
      return `${process.env.REACT_APP_API_ENDPOINT}${normalizedRoute}`;
    }
  }

  /**
   * GET Request
   */
  async get<T = any>(
    route: string,
    payload: RequestPayload = {},
    language: string = "DE",
  ): Promise<APIResponse<T>> {
    try {
      const preparedPayload = await this.preparePayload(payload, language);
      const url = this.getUrl(route);

      const response: AxiosResponse<APIResponse<T>> = await axios.get(url, {
        params: preparedPayload,
        headers: this.getHeaders(language),
      });

      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  /**
   * POST Request
   */
  async post<T = any>(
    route: string,
    payload: RequestPayload = {},
    language: string = "DE",
  ): Promise<APIResponse<T>> {
    try {
      const preparedPayload = await this.preparePayload(payload, false);
      const url = this.getUrl(route);

      const response: AxiosResponse<APIResponse<T>> = await axios.post(
        url,
        JSON.stringify(preparedPayload),
        {
          params: preparedPayload,
          headers: this.getHeaders(language),
        },
      );

      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  /**
   * PATCH Request
   */
  async patch<T = any>(
    route: string,
    payload: RequestPayload = {},
    language: string = "DE",
  ): Promise<APIResponse<T>> {
    try {
      const preparedPayload = await this.preparePayload(payload);
      const url = this.getUrl(route);

      const response: AxiosResponse<APIResponse<T>> = await axios.patch(
        url,
        preparedPayload,
        {
          headers: this.getHeaders(language),
        },
      );

      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  /**
   * DELETE Request
   */
  async delete<T = any>(
    route: string,
    payload: RequestPayload = {},
    language: string = "DE",
  ): Promise<APIResponse<T>> {
    try {
      const preparedPayload = await this.preparePayload(payload);
      const url = this.getUrl(route);

      const response: AxiosResponse<APIResponse<T>> = await axios.delete(url, {
        params: preparedPayload,
        headers: this.getHeaders(language),
      });

      return this.handleResponse(response);
    } catch (error) {
      return this.handleError(error as AxiosError);
    }
  }

  /**
   * Response Handler
   */
  private handleResponse<T>(
    response: AxiosResponse<APIResponse<T>>,
  ): APIResponse<T> {
    // Prüfe auf JWT Token Fehler
    if (
      response.data?.code === "401" &&
      response.data?.message === "Invalid JWT Token"
    ) {
      this.handleTokenExpired();
    }

    return response.data;
  }

  /**
   * Error Handler
   */
  private handleError(error: AxiosError): APIError {
    console.error("API Error:", error);

    if (error.response) {
      return {
        error: error.response.status,
        message:
          (error.response.data as any)?.message || "Ein Fehler ist aufgetreten",
        data: error.response.data,
      };
    }

    return {
      error: "NETWORK_ERROR",
      message: error.message || "Netzwerkfehler",
    };
  }

  /**
   * Handler für abgelaufene Tokens (kannst du überschreiben oder per Callback erweitern)
   */
  private handleTokenExpired(): void {
    console.warn("Token expired - implement logout logic");
    // Hier kannst du einen Event emittieren oder einen Callback aufrufen
    // window.location.href = '/login';
  }

  /**
   * Setzt einen benutzerdefinierten Token-Expired-Handler
   */
  onTokenExpired(callback: () => void): void {
    this.handleTokenExpired = callback;
  }
}

// Singleton-Instanz exportieren
const apiService = new APIService();
export default apiService;

// Exportiere auch die Types für Verwendung in anderen Dateien
export type { APIResponse, APIError, RequestPayload, GraphQLResponse };
